import { useCallback, useEffect, useState } from 'react';
import '../backlog/backlog.css';
import './reagentprep.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormReportsMenu } from '../../components/FormReportsMenu';
import { FormTitle } from '../../components/FormTitle';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { ClassifyDialog } from './ClassifyDialog';
import { PrepNotepadDialog } from './PrepNotepadDialog';
import { draftToDisplayRow, type PrepNotepadDraft } from './prepNotepadDraft';
import { opensPrepNotepadOnNew } from './prepForms';
import ReagentPrepDetailPage from './ReagentPrepDetailPage';
import { deleteReagentPreps, fetchComponents, fetchLookup, fetchReagentPreps, saveComponents, type PrepNotepadComponentDto } from './api';
import { REAGENT_RANGES, rangeCode, rangeDays, type ReagentDateFilter, type ReagentPrepComponent, type ReagentPrepListRow } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation → Reagent Prep Log (nav `rgp-preplog`).
// Port of legacy ALPACA `ReagentPreparation_ListView`; spec §7.
//
// Columns and sort come straight from the legacy Model.xafml: LLT · Division ·
// PrepType · PrepName · ExpirationDate · PreparedBy · DatePrepared, sorted
// DatePrepared DESC then LLT DESC.
//
// WAVE 1 SCOPE — this list is read-only.  New / Edit / Delete open the three
// header forms (Chemistry, Micro Media, Calibration) and the Prep Notepad,
// which land in the next wave; the buttons are deliberately absent rather than
// present-but-dead, so nothing on screen promises an action it cannot perform.
// Prep Report and Preview Label are hidden in legacy too (§20 quirk 15) and
// arrive with the report endpoint.  Everything the list itself does — date
// scoping, per-column filters, sort, export — is live.
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS: AppColumn<ReagentPrepListRow>[] = [
  { field: 'llt',            header: 'LLT',            width: 120 },
  { field: 'division',       header: 'Division',       width: 200 },
  { field: 'prepType',       header: 'PrepType',       width: 110 },
  { field: 'prepName',       header: 'PrepName',       width: 220 },
  // Dates render through AppDataGrid's `type`, which uses the shared fmtLocal*
  // helpers — MM/DD/YYYY, 24-hour, never inline formatting (§14b.6 / C2).
  { field: 'expirationDate', header: 'ExpirationDate', width: 120, type: 'date' },
  { field: 'preparedBy',     header: 'PreparedBy',     width: 130 },
  { field: 'datePrepared',   header: 'DatePrepared',   width: 150, type: 'datetime' },
];

/** What the page is showing. `list` is the landing view (rule 10); a detail is
 *  reached only by New or by selecting a row. */
type Screen =
  | { kind: 'list' }
  | { kind: 'detail'; id: string | null; newPrepType?: number };

/**
 * The Prep Notepad's state, and WHY it is driven from here rather than from the
 * detail page: the Notepad opens on TWO different paths (spec §8 / §9), and on
 * one of them there is no detail page yet.
 *
 *   `create`  — Classify → New Prep → Chemical or Micro Media. XAF opens the
 *               Notepad FIRST, with no parent record; its OK then creates the
 *               header from the buffer and opens the header form in Edit.
 *   `existing`— the Chemistry / Micro header form's "Prep Notepad" button,
 *               editing the components of a record that already exists.
 *
 * Calibration never appears here: it is the one category with no Prep Notepad,
 * and Classify sends it straight to its header form.
 */
type Notepad =
  | { mode: 'create'; prepSelectType: number }
  | { mode: 'existing'; prepSelectType: number; prepId: string; components: ReagentPrepComponent[];
      /** Open on this component (a row click in the components grid). */
      startComponentId?: number | null };

export default function ReagentPrepLogPage() {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [screen, setScreen]   = useState<Screen>({ kind: 'list' });
  const [classify, setClassify] = useState(false);
  const [notepad, setNotepad] = useState<Notepad | null>(null);
  /** Bumped after the Notepad's OK writes an EXISTING prep's components. */
  const [componentsVersion, setComponentsVersion] = useState(0);
  /** A NEW prep's buffered components, shaped for the grid until the first Save. */
  const [pendingRows, setPendingRows] = useState<ReagentPrepComponent[] | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [rows, setRows]       = useState<ReagentPrepListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  // null until the first response tells us which filter the server applied —
  // the initial chip is the tenant default from Settings → Default Settings
  // (spec §7), so the page must not assume one before asking.
  const [dateFilter, setDateFilter] = useState<ReagentDateFilter | null>(null);

  const load = useCallback(async (filter?: ReagentDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchReagentPreps(filter);
      setRows(res.rows);
      setDateFilter(res.dateFilter);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load reagent preparations.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  // First load asks the server for the tenant default; later loads pass the chip.
  useEffect(() => { void load(); }, [load]);

  /** Edit — opens the record in ITS OWN form. Which of the three opens is
   *  decided by the row's prepSelectType, exactly as XAF's
   *  EditAction_Executing (:767-790) picks the DetailView. */
  function openRow(row: ReagentPrepListRow) {
    setScreen({ kind: 'detail', id: row.oid, newPrepType: row.prepSelectType ?? 0 });
  }

  /** Delete — the list action. Soft-deletes each header AND its components. */
  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one record to delete.'); return; }
    const ok = await confirm({
      title: 'Delete?',
      message: `Delete ${selected.size} preparation(s)? Their components are deleted with them.`,
      tone: 'danger', confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      const res = await deleteReagentPreps([...selected].map(String));
      setSelected(new Set());
      await load(dateFilter ?? undefined);
      notify.success(`Deleted ${res.deleted} preparation(s).`);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to delete.');
    }
  }

  /**
   * Classify → "New Prep" — THE per-category routing (spec §8 tab 1):
   *   Chemical / Micro Media → the Prep Notepad popup FIRST, no parent record.
   *   Calibration            → straight to its header form; it has no Notepad.
   */
  function startNewPrep(prepSelectType: number) {
    setClassify(false);
    if (opensPrepNotepadOnNew(prepSelectType)) setNotepad({ mode: 'create', prepSelectType });
    else setScreen({ kind: 'detail', id: null, newPrepType: prepSelectType });
  }

  /** Prep Notepad OK. On the CREATE path the header does not exist yet, so it
   *  is created from the current form defaults first, the buffer is attached,
   *  and the header form opens in Edit — legacy's §9.2 step 3. */
  async function notepadOk(rows: PrepNotepadDraft[]) {
    if (!notepad) return;
    // Draft → wire DTO. Ids, not names (the grid-lookups carry the Oid); the
    // two Exp Dates are omitted because the server derives them from the lot.
    const dto: PrepNotepadComponentDto[] = rows.map((d) => ({
      componentID: d.componentID,
      vendorStockId: d.vendorStockId || null,
      ltId: d.ltId || null,
      labStockId: d.labStockId || null,
      lltId: d.lltId || null,
      solvent: d.solvent,
      calculationApproachId: d.calculationApproachId || null,
      w1UnitsId: d.w1UnitsId || null,
      v1UnitsId: d.v1UnitsId || null,
      c1UnitsId: d.c1UnitsId || null,
      v2UnitsId: d.v2UnitsId || null,
      c2UnitsId: d.c2UnitsId || null,
      stockConc_C1: d.stockConc_C1 || null,
      volumeTaken_V1: d.volumeTaken_V1 || null,
      weight_g_w1: d.weight_g_w1 || null,
      finalVol_V2: d.finalVol_V2 || null,
      finalConc_C2: d.finalConc_C2 || null,
      purity: d.purity || null,
      density: d.density || null,
      mw: d.mw || null,
      eqWt: d.eqWt || null,
      constant: d.constant || null,
      formula: d.formula || null,
      comment: d.comment || null,
      phCriteria: d.phCriteria || null,
      ph: d.ph || null,
      autoclave: d.autoclave || null,
      filterSterilization: d.filterSterilization || null,
      sporeGrowth: d.sporeGrowth || null,
      positiveControl: d.positiveControl || null,
      negativeControl: d.negativeControl || null,
    }));

    try {
      if (notepad.mode === 'existing') {
        await saveComponents(notepad.prepId, dto);
        setNotepad(null);
        // The detail page is already open on this id, so it is NOT remounted —
        // that would discard unsaved header edits. Bump the version so it re-reads
        // just the components and the grid shows what OK just wrote.
        setComponentsVersion((v) => v + 1);
        setScreen({ kind: 'detail', id: notepad.prepId, newPrepType: notepad.prepSelectType });
        void load(dateFilter ?? undefined);
        return;
      }
      // CREATE path: the header has no required fields filled yet, so it cannot
      // be persisted here (PrepType / PrepName / Expiration / Storage are all
      // required server-side). Legacy has the same shape — its new header is
      // unsaved until the user completes the form — so the buffer is carried
      // into the detail page and written on its first Save.
      setNotepad(null);
      setScreen({ kind: 'detail', id: null, newPrepType: notepad.prepSelectType });
      setPendingComponents(dto);
      // Show the buffered components in the grid now, before the first Save
      // writes them — with unit / approach NAMES, not ids.
      try {
        const [u, a] = await Promise.all([fetchLookup('units'), fetchLookup('approaches')]);
        const names = {
          units: new Map(u.map((o) => [o.id, o.name ?? ''] as const)),
          approaches: new Map(a.map((o) => [o.id, o.name ?? ''] as const)),
        };
        setPendingRows(rows.map((d) => draftToDisplayRow(d, names)));
      } catch {
        setPendingRows(rows.map((d) => draftToDisplayRow(d, { units: new Map(), approaches: new Map() })));
      }
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to save the components.');
    }
  }

  /** Components entered in the Notepad before the header existed; written by
   *  the detail page immediately after its first Save mints the record. */
  const [pendingComponents, setPendingComponents] = useState<PrepNotepadComponentDto[] | null>(null);

  const notepadDialog = notepad && (
    <PrepNotepadDialog
      prepSelectType={notepad.prepSelectType}
      existing={notepad.mode === 'existing' ? notepad.components : []}
      startComponentId={notepad.mode === 'existing' ? notepad.startComponentId ?? null : null}
      onOk={(rows) => void notepadOk(rows)}
      onClose={() => setNotepad(null)}
    />
  );

  const classifyDialog = classify && (
    <ClassifyDialog
      onNewPrep={startNewPrep}
      onCopied={(id) => {
        setClassify(false);
        setScreen({ kind: 'detail', id });
        void load(dateFilter ?? undefined);
      }}
      onClose={() => setClassify(false)}
    />
  );

  // ── Detail view (New or Edit) — replaces the list, never a popup (rule 12)
  if (screen.kind === 'detail') {
    return (
      <>
        <ReagentPrepDetailPage
          key={screen.id ?? `new-${screen.newPrepType}`}
          id={screen.id}
          newPrepType={screen.newPrepType}
          pendingComponents={pendingComponents}
          pendingRows={pendingRows}
          componentsVersion={componentsVersion}
          onComponentsFlushed={() => { setPendingComponents(null); setPendingRows(null); }}
          siblingIds={rows.map((r) => r.oid)}
          onNavigate={(nid) => {
            const row = rows.find((r) => r.oid === nid);
            setScreen({ kind: 'detail', id: nid, newPrepType: row?.prepSelectType ?? 0 });
          }}
          onClose={() => setScreen({ kind: 'list' })}
          // New on a DETAIL view reopens the Classify popup too (XAF :3810).
          onNew={() => setClassify(true)}
          onPrepNotepad={(prep, startComponentId) => {
            if (!prep.oid) return;
            void (async () => {
              setNotepad({
                mode: 'existing',
                prepSelectType: prep.prepSelectType ?? 0,
                prepId: prep.oid,
                components: await fetchComponents(prep.oid),
                startComponentId: startComponentId ?? null,
              });
            })();
          }}
          onCreated={(id) => setScreen({ kind: 'detail', id, newPrepType: screen.newPrepType })}
          onChanged={() => void load(dateFilter ?? undefined)}
        />
        {classifyDialog}
        {notepadDialog}
      </>
    );
  }

  return (
    <div className="bl-page">
      <FormTitle pageId="rgp-preplog" loadMs={loadMs} loading={loading}
                 auditFormName="Reagent Prep Log" />
      {/* XAF action set for this list (ctrl TargetViewId map :66-79):
          Date Filter · Reports ▾ · New · Edit · Delete. The hardcoded Prep Report /
          Preview Label buttons are hidden in legacy (:71/:73) because the
          lab-assigned Reports ▾ (Form Report Permission) supersedes them. */}
      <AppToolbar
        left={
          <>
            <DateRangeSelect
              variant="dropdown" label="Date Filter" ranges={REAGENT_RANGES}
              value={dateFilter ? rangeDays(dateFilter) : null}
              onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
            />
            <FormReportsMenu
              reportsUrl="/api/v1/reagent-preps/reports?view=list"
              pdfUrl="/api/v1/reagent-preps/report-pdf"
              keysParam="llts"
              selectedKeys={rows.filter((r) => selected.has(r.oid) && r.llt).map((r) => r.llt!)}
            />
          </>
        }
        // New opens the Classify popup, NOT a blank record (XAF cancels the
        // default new-object behaviour, :3812).
        onNew={() => setClassify(true)}
        onEdit={() => {
          const row = rows.find((r) => r.oid === String([...selected][0]));
          if (row) openRow(row);
        }}
        editDisabled={selected.size !== 1}
        onDelete={() => void deleteSelected()}
        deleteDisabled={selected.size === 0}
        onRefresh={() => load(dateFilter ?? undefined)}
        refreshing={loading}
        exportData={{ filename: 'reagent-prep-log', ...gridExportMatrix(rows, COLUMNS) }}
      />
      <AppDataGrid<ReagentPrepListRow>
        rows={rows}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        // Legacy opens the row's form on double-click (:110 CustomProcessSelectedItem).
        onRowDoubleClick={openRow}
        emptyText="No reagent preparations found for this date range."
      />
      {classifyDialog}
      {notepadDialog}
    </div>
  );
}
